import type { TFunction } from 'i18next'
import { z } from 'zod'

import {
  type ProjectDomainUpdateModel,
  ProjectDomainUpdateModelSchema,
  type TagsByCategoryMap,
  TagsByCategoryMapSchema,
} from '@mntn-dev/domain-types'

import {
  isDateInFuture,
  isDateLessThan,
  isDateTooSmall,
} from '../refinements/date.ts'
import { isFormattedTextLengthValid } from '../refinements/formatted-text.ts'

const RequiredTagsByCategoryMapSchema = TagsByCategoryMapSchema.and(
  z.object({
    language: TagsByCategoryMapSchema.valueType.min(1).max(5),
    location: TagsByCategoryMapSchema.valueType.max(5),
  })
)

const getProjectDetailsRequiredFieldsSchema = (t: TFunction<'validation'>) =>
  z.object({
    name: ProjectDomainUpdateModelSchema.shape.name.transform((val) =>
      val?.trim()
    ),
    biddingCloseDate: z
      .date()
      .superRefine(isDateTooSmall(t, { duration: { days: 3 } }).refinement),
    dueDate: z.date().superRefine(isDateInFuture(t).refinement),
    description: z.string().superRefine(
      isFormattedTextLengthValid(t, t('project-form.description'), {
        min: 1,
      }).refinement
    ),
    tags: RequiredTagsByCategoryMapSchema,
  })

export const ProjectDetailsChecklistSchema = (t: TFunction<'validation'>) =>
  getProjectDetailsRequiredFieldsSchema(t).superRefine((val, ctx) =>
    isDateLessThan(t, {
      values: val,
      field: 'dueDate',
      compareField: 'biddingCloseDate',
      translationKeys: {
        field: 'project-form.dueDate',
        compareField: 'project-form.biddingCloseDate',
      },
    }).refinement(val, ctx)
  )

export const ProjectDetailsUpdateFormModelSchema = (
  t: TFunction<'validation'>
) =>
  ProjectDomainUpdateModelSchema.pick({
    thumbnailFileId: true,
    billingSchedule: true,
    invoiceTerms: true,
  })
    .extend(getProjectDetailsRequiredFieldsSchema(t).shape)
    .superRefine((val, ctx) =>
      isDateLessThan(t, {
        values: val,
        field: 'dueDate',
        compareField: 'biddingCloseDate',
        translationKeys: {
          field: 'project-form.dueDate',
          compareField: 'project-form.biddingCloseDate',
        },
      }).refinement(val, ctx)
    )

// TODO - this should be derived from ProjectDetailsUpdateFormModelSchema
// However, the above schema is used for the final submit and does not match the schema used by onProjectUpdate()
export type ProjectDetailsUpdateFormModel = Pick<
  ProjectDomainUpdateModel,
  'name' | 'description' | 'dueDate' | 'thumbnailFileId' | 'biddingCloseDate'
> & {
  tags?: TagsByCategoryMap
}
